import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";

function LanguageSelector() {
  const { i18n, ready } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();

  // Get current language from URL path
  const getCurrentLangFromPath = () => {
    // Normalize path by removing trailing slash
    let path = location.pathname;
    if (path !== "/" && path.endsWith("/")) {
      path = path.slice(0, -1);
    }
    const pathSegments = path.split("/").filter(Boolean);
    if (pathSegments[0] === "tr" || pathSegments[0] === "en") {
      return pathSegments[0];
    }
    return "tr"; // Default to Turkish if no lang in path
  };

  const [currentLang, setCurrentLang] = useState(getCurrentLangFromPath());
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    const langFromPath = getCurrentLangFromPath();

    // Update state based on URL path
    if (langFromPath !== currentLang) {
      setCurrentLang(langFromPath);
    }

    // Sync i18n with URL path language (but don't trigger on every i18n change)
    if (ready && langFromPath) {
      const langFromI18n = i18n.resolvedLanguage || i18n.language || "tr";
      if (langFromPath !== langFromI18n) {
        i18n.changeLanguage(langFromPath).then(() => {
          document.documentElement.lang = langFromPath;
        });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname, ready]);

  const changeLanguage = async (lng) => {
    const langFromPath = getCurrentLangFromPath();
    const currentI18nLang = i18n.resolvedLanguage || i18n.language || "tr";

    // Check if we're already on this language (considering both URL and i18n)
    const effectiveLang = langFromPath || currentI18nLang;
    if (lng === effectiveLang && currentI18nLang === lng) {
      return;
    }

    setIsLoading(true);

    // Show full screen loading overlay
    const loadingOverlay = document.createElement('div');
    loadingOverlay.id = 'language-loading-overlay';
    loadingOverlay.style.cssText = `
      position: fixed;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      display: flex;
      align-items: center;
      justify-content: center;
      background-color: rgba(255, 255, 255, 0.95);
      z-index: 99999;
    `;
    loadingOverlay.innerHTML = `
      <div style="text-align: center;">
        <div style="
          display: inline-block;
          width: 40px;
          height: 40px;
          border: 4px solid #1CA8CB;
          border-top-color: transparent;
          border-radius: 50%;
          animation: spin 0.8s linear infinite;
        "></div>
        <style>
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
        </style>
      </div>
    `;
    document.body.appendChild(loadingOverlay);

    const currentPath = location.pathname;
    // Normalize current path
    let normalizedCurrentPath = currentPath;
    if (currentPath !== "/" && currentPath.endsWith("/")) {
      normalizedCurrentPath = currentPath.slice(0, -1);
    }

    const pathSegments = normalizedCurrentPath.split("/").filter(Boolean);

    // Remove existing language code if present
    if (pathSegments[0] === "tr" || pathSegments[0] === "en") {
      pathSegments.shift();
    }

    const pathWithoutLang =
      pathSegments.length > 0 ? "/" + pathSegments.join("/") : "/";

    // Add new language code (only for non-Turkish)
    const newPath =
      lng === "tr" ? pathWithoutLang : `/${lng}${pathWithoutLang}`;

    try {
      // Update state first for immediate UI feedback
      setCurrentLang(lng);

      // Change i18n language
      await i18n.changeLanguage(lng);

      // Update HTML lang attribute
      document.documentElement.lang = lng;

      // Navigate to new path if different
      if (location.pathname !== newPath && normalizedCurrentPath !== newPath) {
        navigate(newPath, { replace: true });
      }

      // Wait a bit for page to render
      await new Promise(resolve => setTimeout(resolve, 200));
    } catch (error) {
      // Revert state on error
      setCurrentLang(langFromPath);
    } finally {
      // Remove loading overlay
      const overlay = document.getElementById('language-loading-overlay');
      if (overlay) {
        overlay.style.transition = 'opacity 0.3s ease';
        overlay.style.opacity = '0';
        setTimeout(() => {
          overlay.remove();
        }, 300);
      }
      setIsLoading(false);
    }
  };

  const handleClick = () => {
    // Toggle between tr and en
    const newLang = currentLang === "tr" ? "en" : "tr";
    changeLanguage(newLang);
  };

  return (
    <div className="language-selector">
      <button
        type="button"
        onClick={handleClick}
        disabled={isLoading}
        className="language-toggle-btn"
        style={{
          border: "none",
          background: "transparent",
          color: "inherit",
          cursor: isLoading ? "wait" : "pointer",
          padding: "5px 10px",
          fontSize: "14px",
          fontWeight: "500",
          textTransform: "uppercase",
          transition: "opacity 0.2s ease",
          position: "relative",
          minWidth: "40px",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
        onMouseEnter={(e) => {
          if (!isLoading) {
            e.currentTarget.style.opacity = "0.7";
          }
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.opacity = "1";
        }}
        aria-label={`Change language to ${
          currentLang === "tr" ? "English" : "Türkçe"
        }`}
      >
        {isLoading ? (
          <span
            style={{
              display: "inline-block",
              width: "14px",
              height: "14px",
              border: "2px solid currentColor",
              borderTopColor: "transparent",
              borderRadius: "50%",
              animation: "spin 0.6s linear infinite",
            }}
            aria-label="Loading"
          />
        ) : (
          currentLang === "tr" ? "EN" : "TR"
        )}
      </button>
      <style>{`
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
}

export default LanguageSelector;
